import { useTranslate } from "@/helper/translate";
import Select from "react-select";

type DropdownProps<T> = {
  source: T[];
  value: string | number | undefined | null;
  onChange: (value: string | number) => void;
  getLabel: (item: T) => string;
  getValue: (item: T) => string | number;
  className?: string;
  searchable?: boolean;
  disabled?: boolean;
};

export function Dropdown<T>({
  source,
  value,
  onChange,
  getLabel,
  getValue,
  className = "",
  searchable,
  disabled
}: DropdownProps<T>) {
  const getOptionLabel = (item: T) => {
    if (typeof getLabel === "function") {
      return getLabel(item);
    }
    const fallback = item as unknown as Record<string, unknown>;
    return String(fallback?.label ?? item ?? "");
  };

  const getOptionValue = (item: T) => {
    if (typeof getValue === "function") {
      return getValue(item);
    }
    const fallback = item as unknown as Record<string, unknown>;
    const value = fallback?.value ?? fallback?.id ?? item;
    return typeof value === "number" || typeof value === "string" ? value : String(value ?? "");
  };

  const options = source?.map((item) => ({
    label: getOptionLabel(item),
    value: getOptionValue(item)
  }));
  const selected = options?.find(opt => opt.value === value) ?? null;
  const { translate } = useTranslate();
  return (
    <Select
      className={className}
      classNamePrefix="react-select"
      options={options}
      isSearchable={searchable ?? false}
      value={selected}
      isDisabled={disabled}
      placeholder={translate("dd.Select")}
      styles={{
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isFocused ? 'hsl(var(--react-select-hover))' : 'transparent',
          color: state.isFocused ? 'hsl(var(--react-select-hover-text))' : 'hsl(var(--react-select-text))',
          cursor: 'pointer'
        }),
        menu: (base) => ({
          ...base,
          backgroundColor: 'hsl(var(--react-select-control))'
        }),
        menuList: (base) => ({
          ...base,
          backgroundColor: 'hsl(var(--react-select-control))'
        }),
        singleValue: (base) => ({
          ...base,
          color: 'hsl(var(--react-select-text))'
        }),
        placeholder: (base) => ({
          ...base,
          color: 'hsl(var(--react-select-text))'
        }),
        input: (base) => ({
          ...base,
          color: 'hsl(var(--react-select-text))'
        })
      }}
      onChange={(selected) => {
        if (selected) onChange(selected.value);
      }}
    />
  );
}
